import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus, FaFileExcel, FaFileAlt, FaArrowLeft } from 'react-icons/fa';
import Layout from '../components/Layout';
import FormRow from '../components/FormRow';
import ModalShell from '../components/ModalShell';
import { useAuth } from '../context/AuthContext';
import ExportProjectPDF from '../components/ExportProjectPDF';
import ExportWorklogsExcel from '../components/ExportWorklogsExcel';
import {
  listProjectsApi,
  listProjectMembersApi,
  listProjectLogsApi,
  exportWorklogsApi,
  markLogsAsInvoicedApi,
  approveWorklogsApi
} from '../api/http';
import toast, { Toaster } from 'react-hot-toast';
import Select from 'react-select';


/* ================= helpers ================= */
const pad = (n) => String(n).padStart(2, '0');
const ALL_VALUE = '0';

/*html*/
function sanitizeHtml(html = '') { return String(html || '').replace(/<script/gi, '&lt;script'); }
function htmlToPlain(textOrHtml = '') {
  const div = document.createElement('div');
  div.innerHTML = String(textOrHtml ?? '');
  return (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
}
// Fechas ISO compactas para CSV (YYYY-MM-DD HH:mm)
function toIsoMinute(dt) {
  if (!(dt instanceof Date) || isNaN(dt)) return '';
  return dt.toISOString().replace('T', ' ').slice(0, 16);
}
function parseSafeDateParts(dateStr, timeStr) {
  if (!dateStr) return null;
  const s = `${dateStr}T${timeStr || '00:00'}:00`;
  const d = new Date(s);
  return isNaN(d) ? null : d;
}
function parseSafeDateAny(v) {
  if (!v) return null;
  const t = typeof v === 'string' ? v.replace(' ', 'T') : v;
  const d = new Date(t);
  return isNaN(d) ? null : d;
}
function getLogDate(log) {
  if (log?.date) {
    const dateStr = String(log.date);
    const startStr = log?.start || log?.start_time;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const d = parseSafeDateParts(dateStr, startStr);
      if (d) return d;
    }
    const iso = parseSafeDateAny(dateStr);
    if (iso) {
      if (startStr && /^\d{2}:\d{2}$/.test(startStr)) {
        const y = iso.getFullYear(), m = pad(iso.getMonth() + 1), dd = pad(iso.getDate());
        return parseSafeDateParts(`${y}-${m}-${dd}`, startStr);
      }
      return iso;
    }
  }
  const d2 = parseSafeDateAny(log?.started_at || log?.startedAt);
  if (d2) return d2;
  return parseSafeDateAny(log?.created_at || log?.createdAt);
}

// Inicio/fin del log con tolerancia a formatos
function getLogStarted(log) {
  return parseSafeDateAny(log?.started_at || log?.startedAt) || getLogDate(log) || null;
}
function getLogEnded(log) {
  return parseSafeDateAny(log?.ended_at || log?.endedAt) || null;
}
function getLogMonthKey(log) {
  if (log?.date) return String(log.date).slice(0, 7);
  const d = getLogDate(log);
  return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}` : '';
}
function toMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== 'string' || !hhmm.includes(':')) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}
function getHours(log) {
  if (log?.hours != null) return Number(log.hours) || 0;
  const ms = toMinutes(log?.start || log?.start_time);
  const me = toMinutes(log?.end || log?.end_time);
  if (ms != null && me != null && me > ms) return Math.round(((me - ms) / 60) * 100) / 100;
  const sa = parseSafeDateAny(log?.started_at || log?.startedAt);
  const ea = parseSafeDateAny(log?.ended_at || log?.endedAt);
  if (sa && ea && ea > sa) return Math.round((((ea - sa) / 1000 / 60 / 60)) * 100) / 100;
  return 0;
}
function getAmount(log) {
  if (log?.amount != null) return Number(log.amount) || 0;
  const h = getHours(log);
  const rate = Number(log?.rate ?? log?.hourly_rate_snapshot ?? log?.hourly_rate ?? 0) || 0;
  return Math.round(h * rate);
}
function getUserName(log) {
  return (
    log?.user_name ||
    log?.member_name ||
    log?.username ||
    log?.user?.name ||
    log?.user ||
    '—'
  );
}
const fmtCLP = (n) => Number(n || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP' });
function formatCL(dt) { return `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`; }
function mapMemberOption(m) {
  const id = m?.user_id ?? m?.user?.id ?? m?.id ?? m?.member_id;
  const name = m?.user_name ?? m?.user?.name ?? m?.name ?? m?.username ?? '—';
  return { user_id: String(id ?? ''), user_name: String(name ?? '') };
}

function getFilteredLogsByPeriod(logs, period) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  return logs.filter(log => {
    const logDate = getLogDate(log);
    if (!logDate) return false;

    switch (period) {
      case 'day':
        return logDate >= today && logDate < tomorrow;
      case 'month':
        return logDate >= thisMonth;
      case 'all':
      default:
        return true;
    }
  });
}

function getPeriodLabel(period) {
  switch (period) {
    case 'day': return 'Hoy';
    case 'month': return 'Este Mes';
    case 'all': return 'Total';
    default: return '';
  }
}


/* ================= estilos ================= */
const btnGhost = {
  background: '#ffffff',
  border: '1px solid #d1d5db',
  color: '#374151',
  transition: 'all 0.15s ease'
};

const btnIconRow = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px'
};

const btnSecondary = {
  background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
  border: '1px solid #d1d5db',
  color: '#374151',
  fontWeight: 500,
  transition: 'all 0.15s ease',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)'
};

const btnPrimary = {
  background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
  border: '1px solid #111827',
  color: '#ffffff',
  fontWeight: 600,
  transition: 'all 0.15s ease',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
};

// Table cell styles
const td = {
  padding: '12px 8px',
  borderBottom: '1px solid #e5e7eb',
  verticalAlign: 'middle'
};

const tdNowrap = {
  ...td,
  whiteSpace: 'nowrap'
};

const tdEllipsis = {
  ...td,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const tdRight = {
  ...td,
  textAlign: 'right'
};

// Pill and button styles
const pillTag = {
  display: 'inline-block',
  padding: '4px 8px',
  background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
  border: '1px solid #d1d5db',
  borderRadius: '6px',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: '#374151'
};

const pillFacturable = {
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: '6px',
  fontSize: '0.75rem',
  fontWeight: 600
};

const smBtn = {
  display: 'inline-block',
  padding: '4px 8px',
  borderRadius: '6px',
  fontSize: '0.75rem',
  fontWeight: 500,
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'all 0.15s ease'
};

export default function Bitacora() {
  const navigate = useNavigate();
  const { token, user, hasRole } = useAuth();
  const isAdmin = hasRole ? hasRole('admin') : user?.role === 'admin';
  const canViewAll = isAdmin;
  const myId = user?.id != null ? String(user.id) : '';

  // filtros
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
  });
  const [selectedProjectId, setSelectedProjectId] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState(ALL_VALUE);
  const [q, setQ] = useState('');
  const [periodFilter, setPeriodFilter] = useState('month');
  // Nuevos estados para rango de fechas
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [billableFilter, setBillableFilter] = useState('all'); // 'all' | 'paid' | 'pending'

  // paginación
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // datos
  const [projects, setProjects] = useState([]);
  const [membersByProject, setMembersByProject] = useState([]);
  const [globalMembers, setGlobalMembers] = useState([]);
  const [rows, setRows] = useState([]);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewLog, setViewLog] = useState(null);
  const openView = (log) => { setViewLog(log); setViewOpen(true); };
  const closeView = () => { setViewOpen(false); setViewLog(null); };

  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRowExpanded = (logId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  /* cargar proyectos: para programmer, solo "mis" proyectos */
  useEffect(() => {
    (async () => {
      try {
        const mine = !isAdmin;
        const ps = await listProjectsApi(token, mine ? { mine: true } : undefined);
        setProjects(ps || []);
      } catch { setProjects([]); }
    })();
  }, [token, isAdmin]);

  /* cargar miembros según proyecto */
  useEffect(() => {
    (async () => {
      if (selectedProjectId.length === 0) {
        setMembersByProject([]);
        setSelectedMemberId(ALL_VALUE);
        return;
      }

      try {
        const allMembers = new Map();

        for (const projectId of selectedProjectId) {
          const ms = await listProjectMembersApi(projectId, token);
          const active = (ms || []).filter(m => m?.status === 'active' && (m?.unassigned_at ?? null) == null)
            .map(mapMemberOption)
            .filter(m => m.user_id);

          active.forEach(member => {
            allMembers.set(member.user_id, member);
          });
        }

        const uniqueMembers = Array.from(allMembers.values());
        uniqueMembers.sort((a, b) => a.user_name.localeCompare(b.user_name, 'es'));
        setMembersByProject(uniqueMembers);
      } catch { setMembersByProject([]); }
    })();
  }, [selectedProjectId, token]);

  /* cargar logs (traemos hasta 500 por proyecto y paginamos en cliente) */
  const fetchAll = async () => {
    try {
      let list = [];
      // Si no hay proyectos seleccionados, cargar todos
      const scope = selectedProjectId.length > 0
        ? projects.filter(p => selectedProjectId.includes(String(p.id)))
        : projects;

      for (const p of scope) {
        const ls = await listProjectLogsApi(p.id, token, 500);
        list.push(...(ls || []).map(x => ({ ...x, project_id: p.id, project_name: p.name })));
      }
      setRows(list);

      // miembros globales
      const mset = new Map();
      (list || []).forEach(l => {
        const id = l.user_id ?? l.user?.id ?? l.member_id;
        const name = getUserName(l);
        if (id != null && name) mset.set(String(id), { user_id: String(id), user_name: String(name) });
      });
      setGlobalMembers(Array.from(mset.values()).sort((a, b) => a.user_name.localeCompare(b.user_name, 'es')));
    } catch {
      setRows([]); setGlobalMembers([]);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (projects.length) fetchAll(); }, [projects, selectedProjectId, token]);

  // opciones de miembro: si no es admin, bloqueamos al usuario actual
  const memberOptions = canViewAll
    ? (selectedProjectId.length > 0 ? membersByProject : globalMembers)
    : (myId ? [{ user_id: myId, user_name: user?.name || 'Yo' }] : []);

  /* filtros & orden */
  const filtered = useMemo(() => {
    let out = rows || [];

    if (periodFilter === 'range' && (startDate || endDate)) {
      const start = startDate ? new Date(startDate + 'T00:00:00') : null;
      const end = endDate ? new Date(endDate + 'T23:59:59') : null;

      out = out.filter(log => {
        const logDate = getLogDate(log);
        if (!logDate) return false;

        if (start && logDate < start) return false;
        if (end && logDate > end) return false;
        return true;
      });
    } else {
      out = getFilteredLogsByPeriod(out, periodFilter);
    }

    // Filtrar por proyectos seleccionados
    if (selectedProjectId.length > 0) {
      out = out.filter(l => selectedProjectId.includes(String(l.project_id)));
    }

    if (!canViewAll && myId) {
      out = out.filter(l => String(l.user_id ?? l.user?.id ?? l.member_id ?? '') === myId);
    } else if (selectedMemberId !== ALL_VALUE) {
      out = out.filter(l => String(l.user_id ?? l.user?.id ?? l.member_id ?? '') === String(selectedMemberId));
    }

    if (billableFilter === 'paid') {
      out = out.filter(l => Number(l?.billable) === 1);
    } else if (billableFilter === 'pending') {
      out = out.filter(l => Number(l?.billable) === 0);
    }

    if (q.trim()) {
      const qq = q.trim().toLowerCase();
      out = out.filter(l => {
        const proj = (l.project_name || l.project || '').toLowerCase();
        const mem = (getUserName(l) || '').toLowerCase();
        const note = (l.progress_note || l.note || '').toLowerCase();
        return proj.includes(qq) || mem.includes(qq) || note.includes(qq);
      });
    }
    // ordenar desc
    out = out.slice().sort((a, b) => {
      const da = getLogDate(a)?.getTime() ?? 0;
      const db = getLogDate(b)?.getTime() ?? 0;
      return db - da;
    });
    return out;
  }, [rows, periodFilter, startDate, endDate, selectedProjectId, selectedMemberId, billableFilter, q, canViewAll, myId]);

  // paginado en cliente
  const totalRows = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const pageStart = (pageSafe - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, totalRows);
  const pageRows = useMemo(() => filtered.slice(pageStart, pageEnd), [filtered, pageStart, pageEnd]);

  // Totales (del filtrado completo, no solo la página)
  const totals = useMemo(() => {
    const hours = filtered.reduce((s, l) => s + getHours(l), 0);
    const amount = filtered.reduce((s, l) => s + getAmount(l), 0);
    return { rows: filtered.length, hours: Number(hours.toFixed(2)), amount };
  }, [filtered]);

  const pendingTotals = useMemo(() => {
    const pending = filtered.filter(l => Number(l?.billable) === 0);
    const hours = pending.reduce((s, l) => s + getHours(l), 0);
    const amount = pending.reduce((s, l) => s + getAmount(l), 0);
    return { rows: pending.length, hours: Number(hours.toFixed(2)), amount };
  }, [filtered]);

  // reset de página al cambiar filtros
  useEffect(() => { setPage(1); }, [periodFilter, startDate, endDate, selectedProjectId, selectedMemberId, billableFilter, q]);

  //#region Cambios para check all
  const [invoicedIds, setInvoicedIds] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [isMarking, setIsMarking] = useState(false);

  const handleToggleInvoiced = (logId) => {
    setInvoicedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const handleToggleAll = () => {
    if (selectAll) {
      setInvoicedIds(new Set());
    } else {
      const allIds = new Set(pageRows.map(l => l.id).filter(Boolean));
      setInvoicedIds(allIds);
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    const allPageIds = pageRows.map(l => l.id).filter(Boolean);
    const allSelected = allPageIds.length > 0 &&
      allPageIds.every(id => invoicedIds.has(id));
    setSelectAll(allSelected);
  }, [invoicedIds, pageRows]);


  const handleMarkAsInvoiced = async () => {
    if (invoicedIds.size === 0) return;

    toast((t) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div>
          <strong>¿Confirmar marcación?</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem', color: '#64748b' }}>
            Se marcarán <strong>{invoicedIds.size}</strong> registro(s) como facturado(s)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => {
              toast.dismiss(t.id);
            }}
            style={{
              padding: '6px 16px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
              background: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Cancelar
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              await confirmMarkAsInvoiced();
            }}
            style={{
              padding: '6px 16px',
              borderRadius: '6px',
              border: 'none',
              background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 600
            }}
          >
            Confirmar
          </button>
        </div>
      </div>
    ), {
      duration: Infinity,
      position: 'top-center',
      style: {
        minWidth: '400px',
        padding: '16px'
      }
    });
  };

  const confirmMarkAsInvoiced = async () => {
    setIsMarking(true);

    const loadingToast = toast.loading(
      `Marcando ${invoicedIds.size} registro(s)...`
    );

    try {
      const logIdsArray = Array.from(invoicedIds);
      await markLogsAsInvoicedApi(logIdsArray, token);

      setRows(prevRows =>
        prevRows.map(row =>
          invoicedIds.has(row.id)
            ? { ...row, billable: 1 }
            : row
        )
      );

      setInvoicedIds(new Set());
      setSelectAll(false);

      toast.success(
        `${logIdsArray.length} registro(s) marcados como pagados`,
        { id: loadingToast, duration: 4000 }
      );
    } catch (err) {
      console.error('Error al marcar como facturados:', err);
      toast.error(
        `Error: ${err.message || 'No se pudo completar la operación'}`,
        { id: loadingToast, duration: 5000 }
      );
    } finally {
      setIsMarking(false);
    }
  };

  const handleApproveSelected = async () => {
    if (invoicedIds.size === 0) return;

    toast((t) => (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div>
          <strong>¿Confirmar aprobación?</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '0.9rem', color: '#64748b' }}>
            Se aprobarán <strong>{invoicedIds.size}</strong> registro(s)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => { toast.dismiss(t.id); }}
            style={{ padding: '6px 16px', borderRadius: '6px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer', fontSize: '0.875rem' }}
          >
            Cancelar
          </button>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              await confirmApproveSelected(1);
            }}
            style={{ padding: '6px 16px', borderRadius: '6px', border: 'none', background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)', color: 'white', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}
          >
            Aprobar
          </button>
        </div>
      </div>
    ), { duration: Infinity, position: 'top-center', style: { minWidth: '400px', padding: '16px' } });
  };

  const confirmApproveSelected = async (status) => {
    setIsMarking(true);
    const loadingToast = toast.loading(`${status === 1 ? 'Aprobando' : 'Desaprobando'} ${invoicedIds.size} registro(s)...`);

    try {
      const logIdsArray = Array.from(invoicedIds);
      await approveWorklogsApi(logIdsArray, status, token);

      setRows(prevRows =>
        prevRows.map(row =>
          invoicedIds.has(row.id)
            ? { ...row, admin_approved: status }
            : row
        )
      );

      setInvoicedIds(new Set());
      setSelectAll(false);

      toast.success(
        `${logIdsArray.length} registro(s) ${status === 1 ? 'aprobados' : 'desaprobados'}`,
        { id: loadingToast, duration: 4000 }
      );
    } catch (err) {
      console.error('Error al cambiar aprobación en masa:', err);
      toast.error(
        `Error: ${err.message || 'No se pudo completar la operación'}`,
        { id: loadingToast, duration: 5000 }
      );
    } finally {
      setIsMarking(false);
    }
  };

  const handleToggleAdminApproved = async (logId, currentStatus) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      await approveWorklogsApi([logId], newStatus, token);

      setRows(prevRows =>
        prevRows.map(row =>
          row.id === logId
            ? { ...row, admin_approved: newStatus }
            : row
        )
      );
      toast.success(newStatus === 1 ? 'Registro aprobado' : 'Aprobación removida');
    } catch (err) {
      console.error('Error al cambiar aprobación:', err);
      toast.error('No se pudo cambiar el estado de aprobación');
    }
  };

  const projectOptions = projects.map(p => ({
    value: String(p.id),
    label: p.name
  }));

  // Valor seleccionado para react-select
  const selectedProjects = projectOptions.filter(opt =>
    selectedProjectId.includes(opt.value)
  );



  //#endregion

  return (
    <Layout title="Bitácora (Global)">
      <Toaster position="top-center" reverseOrder={false} />
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Bitácora General</h2>
        <Link className="btn" to="/projects" style={{ ...btnGhost, borderRadius: 10, padding: '8px 12px' }}>
          <span style={btnIconRow}><FaArrowLeft /> Volver a Proyectos</span>
        </Link>
      </div>

      {/* Filtros */}
      <div className="filters-card">
        <div className="flex flex-col gap-2 max-w-3xl">
          <FormRow label="Proyectos">
            <Select
              isMulti
              value={selectedProjects}
              onChange={(selected) => {
                setSelectedProjectId(selected ? selected.map(s => s.value) : []);
                setSelectedMemberId(ALL_VALUE);
              }}
              options={projectOptions}
              placeholder="Selecciona uno o más proyectos..."
              noOptionsMessage={() => "No hay proyectos disponibles"}
              closeMenuOnSelect={false}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '44px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  boxShadow: 'none',
                  '&:hover': {
                    border: '1px solid #9ca3af'
                  }
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#e5e7eb',
                  borderRadius: '6px'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: '#374151',
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }),
                multiValueRemove: (base) => ({
                  ...base,
                  color: '#6b7280',
                  ':hover': {
                    backgroundColor: '#d1d5db',
                    color: '#1f2937'
                  }
                })
              }}
            />

            {/* Botones de acción rápida */}
            {projects.length > 0 && (
              <div style={{
                display: 'flex',
                gap: '8px',
                marginTop: '8px',
                fontSize: '0.75rem'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedProjectId(projects.map(p => String(p.id)));
                    setSelectedMemberId(ALL_VALUE);
                  }}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    color: '#374151'
                  }}
                >
                  Seleccionar todos
                </button>
                {selectedProjectId.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedProjectId([]);
                      setSelectedMemberId(ALL_VALUE);
                    }}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      background: 'white',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      color: '#dc2626'
                    }}
                  >
                    Limpiar
                  </button>
                )}
              </div>
            )}
          </FormRow>

          {isAdmin && (
            <FormRow label="Miembro">
              <select
                value={selectedMemberId}
                onChange={(e) => setSelectedMemberId(e.target.value)}
              >
                <option value={ALL_VALUE}>(Todos)</option>
                {memberOptions.map(m => <option key={m.user_id} value={m.user_id}>{m.user_name}</option>)}
              </select>
            </FormRow>
          )}
          <FormRow label="Estado de Pago">
            <select
              value={billableFilter}
              onChange={(e) => setBillableFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="paid">Pagados</option>
              <option value="pending">Pendientes</option>
            </select>
          </FormRow>
          <FormRow label="Buscar">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="proyecto, miembro o texto del avance…"
            />
          </FormRow>
        </div>
      </div>

      {/* Filtro de período */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        marginBottom: '1rem',
        marginTop: '1rem',
        padding: '1rem',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '12px',
        border: '2px solid #e2e8f0'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#1e293b' }}>
            Estadísticas de {periodFilter === 'range' ? 'Rango Personalizado' : getPeriodLabel(periodFilter)} {isAdmin && '(Todos los usuarios)'}
          </h3>
          <select
            value={periodFilter}
            onChange={(e) => {
              setPeriodFilter(e.target.value);
              if (e.target.value !== 'range') {
                setStartDate('');
                setEndDate('');
              }
            }}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              border: '2px solid #e5e7eb',
              background: 'white',
              color: '#1f2937',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: 'pointer',
              outline: 'none',
              transition: 'all 0.2s ease'
            }}
          >
            <option value="day">Hoy</option>
            <option value="month">Este Mes</option>
            <option value="all">Todo</option>
            <option value="range">Rango de Fechas</option>
          </select>
        </div>

        {/* Selector de rango de fechas */}
        {periodFilter === 'range' && (
          <div style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: '1rem',
            background: 'white',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: '1', minWidth: '200px' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                Fecha Inicio
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: '1', minWidth: '200px' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>
                Fecha Fin
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  padding: '0.5rem',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '0.875rem'
                }}
              />
            </div>
            {(startDate || endDate) && (
              <button
                onClick={() => {
                  setStartDate('');
                  setEndDate('');
                }}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  background: 'white',
                  color: '#6b7280',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  marginTop: 'auto'
                }}
              >
                Limpiar
              </button>
            )}
          </div>
        )}
      </div>

      {/* Botonera */}
      <div className="buttons-toolbar">
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <div className="card py-2 px-3 rounded-lg">
            <span className="opacity-85">Registros:</span> <b>{totals.rows}</b>
            {' '}· <span className="opacity-85">Horas:</span> <b>{totals.hours.toFixed(2)}</b>
            {' '}· <span className="opacity-85">Monto:</span> <b>{fmtCLP(totals.amount)}</b>
            {isAdmin && invoicedIds.size > 0 && (
              <>
                {' '}· <span className="opacity-85">Seleccionados:</span> <b>{invoicedIds.size}</b>
              </>
            )}
          </div>

          {/* Card de Pendientes */}
          <div className="card py-2 px-3 rounded-lg">
            <span className="opacity-85">Pendientes:</span> <b>{pendingTotals.rows}</b>
            {' '}· <span className="opacity-85">Horas:</span> <b>{pendingTotals.hours.toFixed(2)}</b>
            {' '}· <span className="opacity-85">Monto:</span> <b>{fmtCLP(pendingTotals.amount)}</b>
          </div>
        </div>

        <div className="buttons-group">
          {isAdmin && invoicedIds.size > 0 && (
            <>
              <button
                className="btn"
                style={{
                  ...btnPrimary,
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  opacity: isMarking ? 0.6 : 1,
                  cursor: isMarking ? 'not-allowed' : 'pointer'
                }}
                onClick={handleMarkAsInvoiced}
                disabled={isMarking}
                title="Marcar registros seleccionados como pagados"
              >
                Pagado ({invoicedIds.size})
              </button>
              <button
                className="btn"
                style={{
                  ...btnPrimary,
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  opacity: isMarking ? 0.6 : 1,
                  cursor: isMarking ? 'not-allowed' : 'pointer'
                }}
                onClick={handleApproveSelected}
                disabled={isMarking}
                title="Aprobar registros seleccionados"
              >
                Aprobar ({invoicedIds.size})
              </button>
            </>
          )}

          <div className="page-size-selector">
            <label>Por página:</label>
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
              {[20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <ExportWorklogsExcel
            logs={filtered}
            filename={`bitacora_${month || new Date().toISOString().slice(0, 7)}_completa`}
            getUserName={getUserName}
            getHours={getHours}
            getAmount={getAmount}
            render={(onExport) => (
              <button
                className="btn"
                onClick={onExport}
                title="Exportar a Excel (Detalle + Resumen) con datos filtrados"
                style={{
                  ...btnSecondary,
                  borderRadius: '8px',
                  padding: '8px 14px',
                  fontWeight: 500
                }}
              >
                <span style={btnIconRow}>
                  <FaFileExcel style={{ color: '#059669' }} />
                  <span className="hidden sm:inline">Excel Completo</span>
                  <span className="sm:hidden"></span>
                </span>
              </button>
            )}
          />

          {/* Excel de Pendientes */}
          <ExportWorklogsExcel
            logs={filtered.filter(l => Number(l?.billable) === 0)}
            filename={`bitacora_pendientes_${month || new Date().toISOString().slice(0, 7)}`}
            getUserName={getUserName}
            getHours={getHours}
            getAmount={getAmount}
            render={(onExport) => (
              <button
                className="btn"
                onClick={onExport}
                disabled={!isAdmin || pendingTotals.rows === 0}
                title={!isAdmin
                  ? 'Solo administradores pueden exportar pendientes'
                  : pendingTotals.rows === 0
                    ? 'No hay registros pendientes para exportar'
                    : 'Exportar solo registros pendientes a Excel'}
                style={{
                  ...btnSecondary,
                  borderRadius: '8px',
                  padding: '8px 14px',
                  fontWeight: 500,
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  border: '1px solid #f59e0b',
                  color: '#92400e',
                  opacity: (!isAdmin || pendingTotals.rows === 0) ? 0.6 : 1,
                  cursor: (!isAdmin || pendingTotals.rows === 0) ? 'not-allowed' : 'pointer'
                }}
              >
                <span style={btnIconRow}>
                  <span className="hidden sm:inline">Excel Pendientes</span>
                </span>
              </button>
            )}
          />

          {selectedProjectId.length === 1 && (
            <>
              <ExportProjectPDF
                project={projects.find(p => String(p.id) === String(selectedProjectId[0]))}
                logs={filtered}
                monthLabel={periodFilter === 'all' ? 'Todos los períodos' : (periodFilter === 'range' ? 'Rango Personalizado' : (periodFilter === 'month' ? 'Este Mes' : 'Hoy'))}
                memberLabel={
                  selectedMemberId === ALL_VALUE
                    ? 'Todos'
                    : (
                      (selectedProjectId.length > 0 ? membersByProject : globalMembers)
                        .find(m => String(m.user_id) === String(selectedMemberId))?.user_name || '—'
                    )
                }
                getUserName={getUserName}
                getHours={getHours}
                getAmount={getAmount}
                buttonProps={{
                  className: 'btn',
                  disabled: !isAdmin,
                  style: { ...btnPrimary, borderRadius: '8px', padding: '8px 12px' },
                  title: !isAdmin
                    ? 'Solo administradores pueden descargar el PDF del proyecto'
                    : 'Descargar PDF del proyecto con los filtros aplicados'
                }}
              />
              {/* PDF de Pendientes */}
              <ExportProjectPDF
                project={projects.find(p => String(p.id) === String(selectedProjectId[0]))}
                logs={filtered.filter(l => Number(l?.billable) === 0)}
                monthLabel={periodFilter === 'all' ? 'Todos los períodos (Pendientes)' : (periodFilter === 'range' ? 'Rango Personalizado (Pendientes)' : (periodFilter === 'month' ? 'Este Mes (Pendientes)' : 'Hoy (Pendientes)'))}
                memberLabel={
                  selectedMemberId === ALL_VALUE
                    ? 'Todos'
                    : (
                      (selectedProjectId.length > 0 ? membersByProject : globalMembers)
                        .find(m => String(m.user_id) === String(selectedMemberId))?.user_name || '—'
                    )
                }
                getUserName={getUserName}
                getHours={getHours}
                getAmount={getAmount}
                filenamePrefix="Pendientes"
                render={(onExport) => (
                  <button
                    className="btn"
                    onClick={onExport}
                    disabled={!isAdmin || pendingTotals.rows === 0}
                    style={{
                      ...btnSecondary,
                      borderRadius: '8px',
                      padding: '8px 12px',
                      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                      border: '1px solid #f59e0b',
                      fontWeight: 600,
                      opacity: (!isAdmin || pendingTotals.rows === 0) ? 0.6 : 1,
                      cursor: (!isAdmin || pendingTotals.rows === 0) ? 'not-allowed' : 'pointer'
                    }}
                    title={!isAdmin
                      ? 'Solo administradores pueden descargar el PDF de pendientes'
                      : pendingTotals.rows === 0
                        ? 'No hay registros pendientes para exportar'
                        : 'Descargar PDF solo con registros pendientes de pago'}
                  >
                    <span style={btnIconRow}>
                      <span className="hidden sm:inline">PDF Pendientes</span>
                    </span>
                  </button>
                )}
              />
            </>
          )}
          <button
            className="btn"
            onClick={() => {
              if (selectedProjectId.length === 1) navigate(`/worklog/${selectedProjectId[0]}`);
              else navigate(`/worklog`);
            }}
            style={{
              ...btnPrimary,
              borderRadius: '8px',
              padding: '8px 16px',
              fontWeight: 600
            }}
            title="Crear un nuevo registro de bitácora"
          >
            <span style={btnIconRow}>
              <FaPlus style={{ fontSize: '0.875rem' }} />
              <span className="hidden sm:inline">Nuevo registro</span>
              <span className="sm:hidden">Nuevo</span>
            </span>
          </button>
        </div>
      </div>


      {/* ====== TABLA FULL-WIDTH CON CHECKBOXES ====== */}
      <div className="data-table-container">
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                {/* Checkbox "Seleccionar todos" */}
                {isAdmin && (
                  <th style={{ width: '40px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleToggleAll}
                      title="Seleccionar/Deseleccionar todos"
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                )}
                {/* Columna de Aprobado (Admin) */}
                {isAdmin && (
                  <th style={{ width: '40px', textAlign: 'center' }} title="Aprobado por administración">Apr.</th>
                )}
                {[
                  { label: 'Fecha y Hora', align: 'left', class: 'col-date' },
                  { label: 'Proyecto', align: 'left', class: 'col-project' },
                  { label: 'Miembro', align: 'left', class: 'col-member' },
                  { label: 'Etiquetas', align: 'left', class: 'col-description hide-mobile' },
                  { label: 'Horas', align: 'right', class: 'col-hours' },
                  { label: 'Pagado', align: 'center', class: 'col-rate hide-mobile' },
                  { label: 'Monto', align: 'right', class: 'col-amount hide-mobile' },
                  { label: 'Avance', align: 'center', class: 'col-description hide-mobile' },
                  { label: 'Acciones', align: 'center', class: 'col-actions' },
                ].map((h) => (
                  <th
                    key={h.label}
                    className={h.class || ''}
                    style={{ textAlign: h.align }}
                  >
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((l, idx) => {
                const dt = getLogDate(l);
                const userName = getUserName(l);
                const hrs = getHours(l);
                const amt = getAmount(l);
                const isPaid = Number(l?.billable) === 1;
                const isChecked = invoicedIds.has(l.id);
                const zebra = idx % 2 ? '#fafbfc' : '#ffffff';

                return (
                  <tr
                    key={l.id ?? `${l.project_id}-${l.date}-${l.start}-${userName}`}
                    style={{
                      background: l.admin_approved === 1 ? '#dcfce7' : (isAdmin && isChecked ? '#f0fdf4' : zebra),
                      transition: 'background 0.2s ease'
                    }}
                  >
                    {/* Checkbox individual - Solo para Admin */}
                    {isAdmin && (
                      <td style={{ ...td, textAlign: 'center', width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleInvoiced(l.id)}
                          style={{ cursor: 'pointer' }}
                          title={isChecked ? 'Desmarcar' : 'Seleccionar registro'}
                        />
                      </td>
                    )}

                    {/* Aprobado por Admin - Solo para Admin */}
                    {isAdmin && (
                      <td style={{ ...td, textAlign: 'center', width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={l.admin_approved === 1}
                          onChange={() => handleToggleAdminApproved(l.id, l.admin_approved)}
                          style={{ cursor: 'pointer' }}
                          title={l.admin_approved === 1 ? 'Quitar aprobación' : 'Aprobar registro'}
                        />
                      </td>
                    )}



                    {/* Fecha y Hora */}
                    <td className="col-date" style={{ ...tdNowrap }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <div className="text-sm font-medium text-slate-900">
                          {dt ? formatCL(dt).split(' ')[0] : (l?.date ? String(l.date).slice(0, 10) : '—')}
                        </div>
                        <div className="text-xs text-slate-500" style={{ fontWeight: 500 }}>
                          {l?.start && l?.end ? `${l.start} - ${l.end}` : (l?.start || l?.end || '—')}
                        </div>
                      </div>
                    </td>

                    {/* Proyecto */}
                    <td className="col-project" style={{ ...tdEllipsis }}>
                      <div className="font-medium text-slate-900" title={l.project_name || l.project}>
                        {l.project_name || l.project}
                      </div>
                    </td>

                    {/* Miembro */}
                    <td className="col-member" style={{ ...tdEllipsis }}>
                      <div className="text-slate-700" title={userName}>
                        {userName}
                      </div>
                    </td>

                    {/* Etiquetas */}
                    <td className="col-description hide-mobile" style={{ ...td }}>
                      {l.activity_code
                        ? <span style={pillTag}>{l.activity_code}</span>
                        : <span className="text-slate-400 text-sm">—</span>}
                    </td>

                    {/* Horas */}
                    <td className="td-num col-hours" style={{ ...tdRight }}>
                      <span className="font-mono font-semibold text-slate-900">
                        {hrs.toFixed(2)}
                      </span>
                    </td>

                    {/* Estado de Pago - ACTUALIZADO */}
                    <td className="col-rate hide-mobile" style={{ ...td, textAlign: 'center' }}>
                      <span
                        title={isPaid ? 'Registro facturado/pagado' : 'Pendiente de pago'}
                        style={{
                          ...pillFacturable,
                          background: isPaid
                            ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                            : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                          border: isPaid ? '1px solid #10b981' : '1px solid #f59e0b',
                          color: isPaid ? '#065f46' : '#92400e'
                        }}
                      >
                        {isPaid ? 'Pagado' : 'Pendiente'}
                      </span>
                    </td>

                    {/* Monto */}
                    <td className="td-num col-amount hide-mobile" style={{ ...tdRight }}>
                      <span className="font-mono font-semibold text-slate-900">
                        {fmtCLP(amt)}
                      </span>
                    </td>

                    {/* Avance */}
                    <td className="col-description hide-mobile" style={{ ...td }}>
                      <div
                        style={{
                          maxWidth: '300px',
                          cursor: 'pointer',
                          color: '#475569'
                        }}
                        onClick={() => toggleRowExpanded(l.id)}
                        title="Click para expandir/contraer el avance"
                      >
                        {(() => {
                          const noteHtml = l.progress_note || l.note || '';
                          const plainText = htmlToPlain(noteHtml);
                          const isExpanded = expandedRows.has(l.id);

                          if (!plainText) return <span className="text-slate-400 text-sm">Sin avance</span>;

                          if (isExpanded) {
                            return (
                              <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {plainText}
                                <div style={{
                                  color: '#3b82f6',
                                  fontWeight: 500,
                                  marginTop: '8px',
                                  fontSize: '0.875rem'
                                }}>
                                  Ver menos
                                </div>
                              </div>
                            );
                          }

                          const shouldTruncate = plainText.length > 100;

                          return (
                            <>
                              <span style={{
                                display: 'inline',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {shouldTruncate ? plainText.slice(0, 100) : plainText}
                              </span>
                              {shouldTruncate && (
                                <span style={{
                                  color: '#3b82f6',
                                  fontWeight: 500,
                                  marginLeft: '4px',
                                  display: 'inline'
                                }}>
                                  ... Ver más
                                </span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </td>

                    {/* Acciones */}
                    <td className="col-actions" style={{ ...td, textAlign: 'center' }}>
                      <Link
                        style={{
                          ...smBtn,
                          background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                          border: '1px solid #22c55e',
                          color: '#15803d',
                          textDecoration: 'none'
                        }}
                        to={`/worklog/${l.project_id}`}
                        title="Abrir el proyecto"
                      >
                        📁 Abrir
                      </Link>
                    </td>
                  </tr>
                );
              })}

              {!pageRows.length && (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} className="text-center py-12 text-slate-500">
                    <div className="flex flex-col items-center gap-3">
                      <div className="text-5xl"></div>
                      <div className="font-semibold text-slate-700 text-lg">Sin registros</div>
                      <div className="text-sm">Prueba ajustando los filtros o el rango de fechas.</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paginación */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
        <div style={{ opacity: .8 }}>
          Mostrando <b>{totalRows ? pageStart + 1 : 0}</b>–<b>{pageEnd}</b> de <b>{totalRows}</b>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={pageSafe <= 1}>← Anterior</button>
          <div style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 8 }}>
            Página <b>{pageSafe}</b> / {totalPages}
          </div>
          <button className="btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={pageSafe >= totalPages}>Siguiente →</button>
        </div>
      </div>

      {/* Modal */}
      {viewOpen && (
        <ModalShell open={viewOpen} onClose={closeView} title="Avance de bitácora">
          <div className="modal-body">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <strong style={{ color: '#374151', fontSize: '0.875rem' }}>Fecha</strong>
                <div style={{ marginTop: '4px', fontWeight: '500' }}>{(() => { const d = getLogDate(viewLog); return d ? formatCL(d) : (viewLog?.date || '—'); })()}</div>
              </div>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <strong style={{ color: '#374151', fontSize: '0.875rem' }}>Proyecto</strong>
                <div style={{ marginTop: '4px', fontWeight: '500' }}>{viewLog?.project_name || viewLog?.project || '—'}</div>
              </div>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <strong style={{ color: '#374151', fontSize: '0.875rem' }}>Miembro</strong>
                <div style={{ marginTop: '4px', fontWeight: '500' }}>{getUserName(viewLog)}</div>
              </div>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <strong style={{ color: '#374151', fontSize: '0.875rem' }}>Horas</strong>
                <div style={{ marginTop: '4px', fontWeight: '600', color: '#059669' }}>{getHours(viewLog).toFixed(2)}</div>
              </div>
              <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <strong style={{ color: '#374151', fontSize: '0.875rem' }}>Monto</strong>
                <div style={{ marginTop: '4px', fontWeight: '600', color: '#dc2626' }}>{fmtCLP(getAmount(viewLog))}</div>
              </div>
              {viewLog?.activity_code && (
                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                  <strong style={{ color: '#374151', fontSize: '0.875rem' }}>Etiqueta</strong>
                  <div style={{ marginTop: '4px' }}>
                    <span style={{ ...pillTag, background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', border: '1px solid #3b82f6', color: '#1e40af' }}>
                      {viewLog.activity_code}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div style={{
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              padding: '16px',
              maxHeight: '400px',
              overflow: 'auto',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
              lineHeight: '1.6'
            }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(viewLog?.progress_note || viewLog?.note || '') }} />
          </div>
        </ModalShell>
      )}
    </Layout>
  );
}